import { Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Icon } from "@mui/material";

import SoftTypography from "components/SoftComponents/SoftTypography";
import SoftInput from "components/SoftComponents/SoftInput";
import SoftButton from "components/SoftComponents/SoftButton";
import SoftSelect from "components/SoftComponents/SoftSelect";

import SprayCan from "assets/images/spray_can_white.png";
import BrokenDevice from "assets/images/broken_device_white.png";

import { conditionConfig, conditionOptions, actionOptions, visitTypeOptions } from "utils/inspection";
import { useEffect, useMemo } from "react";
import SoftBox from "components/SoftComponents/SoftBox";

// Reusable styling for the option boxes
const optionBoxStyles = (isChecked) => ({
    display: 'flex',
    alignItems: 'center',
    border: '1px solid',
    borderColor: isChecked ? 'info.main' : 'grey.300',
    backgroundColor: isChecked ? 'rgba(33, 150, 243, 0.08)' : 'transparent',
    borderRadius: '8px',
    px: 1.5,
    py: 1,
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    userSelect: 'none',
    height: '100%',
    width: '100%',
    boxSizing: 'border-box',
    '&:hover': {
        backgroundColor: isChecked ? 'rgba(33, 150, 243, 0.12)' : 'rgba(0, 0, 0, 0.03)',
    }
});

function InspectionReportingDialog({ reportData, setReportData, isReportModalOpen, setIsReportModalOpen, maintenanceTags, generateReportMutation }) {
    const maintenanceTagOptions = useMemo(() => {
        return maintenanceTags.map(m => ({ label: m.tag_label, value: m.tag_label }));
    }, [maintenanceTags]);

    useEffect(() => {
        if (maintenanceTagOptions.length > 0) {
            setReportData(prev => prev.maintenanceTag ? prev : {
                ...prev,
                maintenanceTag: maintenanceTagOptions[maintenanceTagOptions.length - 1].value
            });
        }
    }, [maintenanceTagOptions]);

    const visitOpts = visitTypeOptions.filter(vt => !(vt.value == 'routine' && !maintenanceTags.length))

    const handleConditionToggle = (condition) => {
        setReportData(prev => ({
            ...prev,
            conditions: prev.conditions.includes(condition)
                ? prev.conditions.filter(c => c !== condition)
                : [...prev.conditions, condition]
        }));
    };

    const handleActionToggle = (action) => {
        setReportData(prev => ({
            ...prev,
            actions: prev.actions.includes(action)
                ? prev.actions.filter(a => a !== action)
                : [...prev.actions, action]
        }));
    };

    const handleGenerateReport = () => {
        // Convert the date strings to epoch (milliseconds) before passing them to the mutation
        var payload = { ...reportData };

        if (reportData.visitType === "routine") {
            const selectedTag = maintenanceTags.find(m => m.tag_label === reportData.maintenanceTag);
            if (selectedTag) {
                payload.start_time = new Date(selectedTag.createdAt).getTime();
                payload.end_time = new Date(selectedTag.completedAt).getTime();
            }
        }
        else {
            payload.start_time = new Date(reportData.startDate).getTime();
            payload.end_time = new Date(reportData.endDate).getTime();
        }

        generateReportMutation.mutate(payload);
    };

    return (
        <Dialog open={isReportModalOpen} onClose={() => setIsReportModalOpen(false)} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: '1rem' } }}>
            <DialogTitle>
                <SoftTypography variant="h5" fontWeight="bold">
                    Generate Inspection Report
                </SoftTypography>
                <SoftTypography variant="body2" color="text">
                    Configure the parameters below to export a summary PDF.
                </SoftTypography>
            </DialogTitle>

            <DialogContent sx={{ overflowY: reportData.visitType == "routine" ? "unset" : "auto" }}>
                <SoftBox pt={1} pb={3} display="flex" gap={2}>
                    <SoftBox flex={1}>
                        <SoftTypography variant="caption" fontWeight="bold" mb={1} display="block" color="text">Visit Type</SoftTypography>
                        <SoftSelect
                            options={visitOpts}
                            value={visitOpts.find(opt => opt.value === reportData.visitType) || visitOpts[0]}
                            onChange={(selected) => setReportData({ ...reportData, visitType: selected.value })}
                            isDisabled={generateReportMutation.isPending}
                        />
                    </SoftBox>
                </SoftBox>

                {reportData.visitType == "routine" ? (
                    <SoftBox pt={1} pb={3} display="flex" gap={2}>
                        <SoftBox flex={1}>
                            <SoftTypography variant="caption" fontWeight="bold" mb={1} display="block" color="text">Maintenance Identifier</SoftTypography>
                            <SoftSelect
                                options={maintenanceTagOptions}
                                value={maintenanceTagOptions.find(opt => opt.value === reportData.maintenanceTag) || maintenanceTagOptions[(maintenanceTagOptions.length - 1)]}
                                onChange={(selected) => setReportData({ ...reportData, maintenanceTag: selected.value })}
                                isDisabled={generateReportMutation.isPending}
                            />
                        </SoftBox>
                    </SoftBox>
                ) : (
                    <>
                        <SoftBox pt={1} pb={3} display="flex" gap={2}>
                            <SoftBox flex={1}>
                                <SoftTypography variant="caption" fontWeight="bold" mb={1} display="block" color="text">Start Date</SoftTypography>
                                <SoftInput
                                    type="date"
                                    value={reportData.startDate}
                                    onChange={(e) => setReportData({ ...reportData, startDate: e.target.value })}
                                    disabled={generateReportMutation.isPending}
                                />
                            </SoftBox>
                            <SoftBox flex={1}>
                                <SoftTypography variant="caption" fontWeight="bold" mb={1} display="block" color="text">End Date</SoftTypography>
                                <SoftInput
                                    type="date"
                                    value={reportData.endDate}
                                    onChange={(e) => setReportData({ ...reportData, endDate: e.target.value })}
                                    disabled={generateReportMutation.isPending}
                                />
                            </SoftBox>
                        </SoftBox>

                        {reportData.visitType != "callout" && (
                            <>
                                <SoftBox pb={4}>
                                    <SoftTypography variant="caption" fontWeight="bold" mb={2} display="block" color="text">
                                        Include Conditions (Optional)
                                    </SoftTypography>
                                    {/* Material UI Grid Implementation */}
                                    <Grid container spacing={1}>
                                        {conditionOptions.map((condition) => {
                                            const isChecked = reportData.conditions.includes(condition);
                                            const badge = conditionConfig[condition];

                                            return (
                                                <Grid item xxs={6} sm={4} md={3} key={condition}>
                                                    <Box sx={optionBoxStyles(isChecked)} onClick={() => !generateReportMutation.isPending && handleConditionToggle(condition)}>
                                                        <SoftBox display="flex" alignItems="center" gap={1.5}>
                                                            {badge.icon === "SprayCan" && (
                                                                <SoftBox component="img" src={SprayCan} variant="gradient" bgColor={badge.color} color="white" sx={{ borderRadius: '4px', width: '24px', height: '24px' }} />
                                                            )}
                                                            {badge.icon === 'BrokenDevice' && (
                                                                <SoftBox component="img" src={BrokenDevice} variant="gradient" bgColor={badge.color} color="white" sx={{ borderRadius: '4px', width: '24px', height: '24px' }} />
                                                            )}
                                                            {(badge.icon !== "BrokenDevice" && badge.icon !== "SprayCan") && (
                                                                <SoftBox variant="gradient" bgColor={badge.color} color="white" sx={{ borderRadius: '4px', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                    <Icon fontSize="22px !important" sx={{ color: "#fff", fontSize: '18px !important' }}>{badge.icon}</Icon>
                                                                </SoftBox>
                                                            )}
                                                            <SoftTypography variant="button" fontWeight={isChecked ? "bold" : "regular"} textTransform="capitalize">
                                                                {condition}
                                                            </SoftTypography>
                                                        </SoftBox>
                                                    </Box>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </SoftBox>

                                <SoftBox pb={1}>
                                    <SoftTypography variant="caption" fontWeight="bold" mb={2} display="block" color="text">
                                        Include Actions (Optional)
                                    </SoftTypography>
                                    {/* Material UI Grid Implementation */}
                                    <Grid container spacing={1}>
                                        {actionOptions.map((action) => {
                                            const isChecked = reportData.actions.includes(action);
                                            return (
                                                <Grid item xxs={6} sm={4} md={3} key={action}>
                                                    <Box sx={optionBoxStyles(isChecked)} onClick={() => !generateReportMutation.isPending && handleActionToggle(action)}>
                                                        <SoftBox display="flex" alignItems="center" height="24px" px={0.5}>
                                                            <SoftTypography variant="button" fontWeight={isChecked ? "bold" : "regular"} textTransform="capitalize">
                                                                {action}
                                                            </SoftTypography>
                                                        </SoftBox>
                                                    </Box>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </SoftBox>
                            </>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 1 }}>
                <SoftButton onClick={() => setIsReportModalOpen(false)} variant="text" color="secondary" disabled={generateReportMutation.isPending}>
                    Cancel
                </SoftButton>
                <SoftButton onClick={handleGenerateReport} variant="gradient" color="info" disabled={generateReportMutation.isPending}>
                    {generateReportMutation.isPending ? <CircularProgress size={20} color="inherit" /> : 'Generate PDF'}
                </SoftButton>
            </DialogActions>
        </Dialog>
    );
}

export default InspectionReportingDialog;